import React, { useContext, useState, useEffect } from "react";
import { auth, firebaseAuth } from "../firebase";
import { isNative, isIOS } from "../utils/platform";
import { linkExistingStripeSubscription } from "../Services/linkExistingStripeService";

export const AuthContext = React.createContext();

export function useAuth() {
  return useContext(AuthContext);
}

// The native bridge is pulled in on demand so the web bundle never loads the
// Capacitor plugin (and the modular firebase/auth it drags behind it).
const nativeAuth = () => import("../utils/nativeAuth");

const stripeLinkAttemptKey = (uid) => `stripe-relink-attempted:${uid}`;

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState();
  const [loading, setLoading] = useState(true);

  function register(email, password) {
    return auth.createUserWithEmailAndPassword(email, password);
  }

  function login(email, password) {
    return auth.signInWithEmailAndPassword(email, password);
  }

  async function logout() {
    // Clear the native provider's cached account first so the next sign-in
    // offers the picker; the webview session is what actually gates the app,
    // so it is signed out last and unconditionally.
    if (isNative()) {
      const { signOutNative } = await nativeAuth();
      await signOutNative();
    }
    return auth.signOut();
  }

  function forgotPassword(email) {
    return auth.sendPasswordResetEmail(email);
  }

  function loginAsGuest() {
    return auth.signInAnonymously();
  }

  async function loginInWithGoogle() {
    // Google refuses OAuth in an embedded webview, so inside the app this has
    // to go through the native sheet. Both paths end at the same place: a
    // credential handed to the JS SDK, so callers see no difference.
    if (isNative()) {
      const { signInWithGoogleNative } = await nativeAuth();
      return signInWithGoogleNative();
    }

    const provider = new firebaseAuth.GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account" });
    provider.addScope("email");
    provider.addScope("profile");
    // Popup can log a COOP warning in Chrome; auth often still succeeds.
    // Only fall back to redirect when the popup is blocked — not when the
    // user closes it (that used to kick off an unexpected full-page redirect).
    return auth.signInWithPopup(provider).catch((error) => {
      if (error?.code === "auth/popup-blocked") {
        return auth.signInWithRedirect(provider);
      }
      throw error;
    });
  }

  // Sign in with Apple. Required by App Store Guideline 4.8 wherever a
  // third-party social login is offered, which is why this exists at all.
  async function loginWithApple() {
    const { signInWithAppleNative } = await nativeAuth();
    return signInWithAppleNative();
  }

  // iOS only for now: the native sheet needs no extra Firebase setup there,
  // whereas Apple-on-Android goes through a web flow that needs a Services ID
  // and key configured first. Flip this to isNative() once that is set up.
  const isAppleSignInAvailable = isNative() && isIOS();

  // Complete Google redirect sign-in (HashRouter-safe on return to app root)
  function completeGoogleRedirectIfPresent() {
    return auth.getRedirectResult();
  }

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Re-attach live Stripe subscriptions after the Firebase project move.
  // Billing already continues in Stripe; this writes users-stripe/{uid} + claims.
  useEffect(() => {
    const user = currentUser;
    if (!user || user.isAnonymous || !user.email) return;

    let cancelled = false;
    const key = stripeLinkAttemptKey(user.uid);
    try {
      if (sessionStorage.getItem(key) === "1") return;
    } catch (error) {
      // sessionStorage may be blocked; still attempt the link once per mount.
    }

    (async () => {
      try {
        const result = await linkExistingStripeSubscription();
        if (cancelled) return;
        try {
          sessionStorage.setItem(key, "1");
        } catch (error) {
          // ignore
        }
        if (result?.linked) {
          // Refresh custom claims (stripeRole) for paid AI metering.
          await user.getIdToken(true);
        }
      } catch (error) {
        // Non-fatal: grandfather email list / free tier still apply.
        console.warn("Stripe subscription relink skipped", error?.message || error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [currentUser?.uid, currentUser?.email]);

  const value = {
    currentUser,
    register,
    login,
    logout,
    forgotPassword,
    loginAsGuest,
    loginInWithGoogle,
    loginWithApple,
    isAppleSignInAvailable,
    completeGoogleRedirectIfPresent,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
